/**
 * The two things a project asks of the person's own AI, because Vephus cannot do either itself.
 *
 * **SYNC** is what a project needs first: DevPulse starts empty and the writing already exists in
 * the repository, so the agent working in it mirrors the documentation across through MCP.
 *
 * **% done** is the question a manager asks and no count can answer: DevPulse knows what agents
 * reported, not whether what an SDD describes is built. The agent reads both and says.
 *
 * Both go out as remote-control commands, shown in full before they are sent, and both say what
 * they need — including the two things DevPulse cannot check: that the agent has the repository
 * open, and that what it will read is up to date.
 */

import { useQuery } from '@tanstack/react-query'
import { Gauge, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { AskAgent } from './AskAgent'
import { estimatePrompt } from '../lib/estimatePrompt'
import { syncDocsPrompt } from '../lib/syncPrompt'

export function ProjectAgentActions({ project }: { project: { id: string; key: string; name: string } }) {
  const { t } = useI18n()

  // Only fetched once a panel is opened; both panels use the same list.
  const { data: specs } = useQuery({ queryKey: ['specs', {}], queryFn: () => api.specs.list() })
  const mine = (specs?.specs ?? []).filter((spec) => spec.projectId === project.id)

  return (
    <div className="row row--tight row--wrap">
      <AskAgent
        label={t('sync.ask')}
        icon={<RefreshCw size={13} aria-hidden />}
        what={t('sync.what', { project: project.name })}
        needs={[t('sync.needRepo'), t('sync.needDocs')]}
        prompt={syncDocsPrompt(project, mine)}
        sent={t('sync.sent')}
        failed={t('estimate.failed')}
      />

      <AskAgent
        label={t('estimate.ask')}
        icon={<Gauge size={13} aria-hidden />}
        what={t('estimate.what', { project: project.name })}
        needs={[t('estimate.needRepo'), t('estimate.needDocs')]}
        prompt={estimatePrompt(project, mine)}
        sent={t('estimate.sent')}
        failed={t('estimate.failed')}
        // Belt and braces: the next agent that works on this project is asked in its tool result
        // too, in case none is listening right now. Managers only, so a refusal is ignored.
        after={() => api.specs.requestEstimate(project.id)}
      />
    </div>
  )
}
